import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AxiosError } from "axios";
import { Constants } from "@eagerdog/constants";
import { ICourseSessionRegistration, ICourseSummary, ITransactionUserSummary, IUser, QueryDto } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { helperService } from "src/services/helper.service";
import { useTabContent } from "src/components/TabContent/TabContent";
import { toast } from "src/components/Toast/ToastManager";
import CourseRegistrations from "src/routes/Course/CourseRegistrations/CourseRegistrations";
import CourseTransactions from "src/routes/Course/CourseTransactions/CourseTransactions";
import CourseWaitlistTab from "./CourseWaitlistTab";

import CourseManageSidebar from "./CourseManageSidebar";
import {
  getManageCourseSectionIndex,
  getRegistrantSectionLabel,
  MANAGE_COURSE_REGISTRANTS_TAB_INDEX,
  MANAGE_COURSE_TRANSACTIONS_TAB_INDEX,
  MANAGE_COURSE_WAITLIST_TAB_INDEX,
} from "./manage-course-sections";

import styles from "./CourseManage.module.scss";

const successTransactionsQuery: QueryDto = {
  query: {
    $and: [
      {
        attribute_name: "status",
        attribute_value: {
          operator: "$eq",
          value: "success",
        },
      },
    ],
  },
};

const CourseManage: React.FC = () => {
  const navigate = useNavigate();
  const { cid } = useParams<{ cid: string }>();
  const user: IUser = helperService.getUser();
  const { activeTab, setTab } = useTabContent();
  const [loaded, setLoaded] = useState(false);
  const [course, setCourse] = useState<ICourseSummary>();
  const [registrations, setRegistrations] = useState<ICourseSessionRegistration[]>([]);
  const [transactions, setTransactions] = useState<ITransactionUserSummary[]>([]);
  const [registrationsLoaded, setRegistrationsLoaded] = useState(false);
  const [transactionsLoaded, setTransactionsLoaded] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sessionFilter] = useState<number | undefined>(() => {
    const raw = new URLSearchParams(window.location.search).get("session");
    if (!raw) {
      return undefined;
    }
    const session = parseInt(raw, 10);
    return Number.isNaN(session) ? undefined : session;
  });

  const registrantLabel = getRegistrantSectionLabel(course?.course_type);
  const pageLabel = activeTab === MANAGE_COURSE_TRANSACTIONS_TAB_INDEX
    ? "Transactions"
    : activeTab === MANAGE_COURSE_WAITLIST_TAB_INDEX
      ? "Waitlist"
      : registrantLabel;
  const courseUrl = `/c/${cid}`;

  const hasAdminAccess = useCallback(async (summary: ICourseSummary) => {
    if (summary.created_by === user._id) {
      return true;
    }

    if (user.user_type === Constants.user_type.club_manager) {
      const capacities = helperService.getCapacities();
      return capacities.some(
        (cap: any) =>
          cap.club_id?.toString() === summary.club_id?.toString() &&
          cap.capacities?.includes(Constants.capacity_type.admin)
      );
    }

    if (user.user_type === Constants.user_type.owner) {
      try {
        const capacitiesResponse = await apiService.getUserCapacities();
        return capacitiesResponse.capacities.some(
          (capacity) =>
            capacity.club_id === summary.club_id &&
            capacity.capacities.includes(Constants.capacity_type.admin)
        );
      } catch {
        return false;
      }
    }

    return false;
  }, [user._id, user.user_type]);

  const showError = useCallback((content: string, url: string) => {
    toast.show({
      title: "Manage Course",
      content,
      duration: 10000,
      type: "fail",
    });
    navigate(url);
  }, [navigate]);

  const loadTransactions = useCallback((courseId: string) => {
    return apiService.getCourseTransactions(courseId, successTransactionsQuery).then((response) => {
      setTransactions(response);
    }).finally(() => {
      setTransactionsLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (loaded || !cid) {
      return;
    }

    const queryParameters = new URLSearchParams(window.location.search);
    const paramSession = queryParameters.get("session");
    const paramTab = queryParameters.get("tab");

    if (paramSession) {
      setTab(MANAGE_COURSE_REGISTRANTS_TAB_INDEX);
    } else if (paramTab) {
      const tabIndex = getManageCourseSectionIndex(paramTab);
      if (tabIndex !== undefined) {
        setTab(tabIndex);
      }
    }

    const loadCourse = async () => {
      try {
        const summary = await apiService.getCourseSummaryForHandle(cid);

        if (summary.status === Constants.course_status.archived) {
          showError("Course has been deleted", "/courses");
          setLoaded(true);
          return;
        }

        const allowed = await hasAdminAccess(summary);
        if (!allowed) {
          showError("Only club admins can manage a course", `/c/${summary.handle || cid}`);
          setLoaded(true);
          return;
        }

        setCourse(summary);
      } catch (error) {
        showError("Course not found", "/courses");
      }

      setLoaded(true);
    };

    loadCourse();
  }, [loaded, cid, hasAdminAccess, setTab, showError]);

  useEffect(() => {
    if (!course) {
      return;
    }

    if (activeTab === MANAGE_COURSE_REGISTRANTS_TAB_INDEX && !registrationsLoaded) {
      apiService.getCourseSessionRegistrations(course._id, {}).then((response) => {
        setRegistrations(response);
      }).catch((error: AxiosError) => {
        toast.show({
          title: "Manage Course",
          content: "Could not load registrants",
          duration: 8000,
          errorDetails: error,
          type: "fail",
        });
      }).finally(() => {
        setRegistrationsLoaded(true);
      });
    }

    if (activeTab === MANAGE_COURSE_TRANSACTIONS_TAB_INDEX && !transactionsLoaded) {
      loadTransactions(course._id).catch((error: AxiosError) => {
        toast.show({
          title: "Manage Course",
          content: "Could not load transactions",
          duration: 8000,
          errorDetails: error,
          type: "fail",
        });
      });
    }
  }, [course, activeTab, registrationsLoaded, transactionsLoaded, loadTransactions]);

  return (
    <div className={styles.CourseManage}>
      {course && <Helmet>
        <title>{course.title} - {pageLabel}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>}
      <CourseManageSidebar
        courseTitle={course?.title}
        clubName={course?.club_name || course?.club?.club_name}
        clubLogoUrl={course?.club?.logo_url}
        courseUrl={course ? `/c/${course.handle}` : courseUrl}
        registrantLabel={registrantLabel}
        activeTab={activeTab}
        onSelect={setTab}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className={styles.main}>
        <div className={styles.inner}>
          <div className={styles.pageHeader}>
            <div className={styles.breadcrumb}>
              <div className={styles.path}>
                <Link to={course ? `/c/${course.handle}` : courseUrl}>{course ? course.title : "Course"}</Link> / </div><div className={styles.current}>{pageLabel}
              </div>
            </div>
            <button
              type="button"
              className={styles.mobileMenu}
              onClick={() => setMobileNavOpen(true)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h10" />
              </svg>
              Course menu
            </button>
          </div>
          <h1>{pageLabel}</h1>
          {course && <div className={styles.sections}>
            <div className={activeTab === MANAGE_COURSE_REGISTRANTS_TAB_INDEX ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <CourseRegistrations
                course={course}
                registrations={registrations}
                sessionFilter={sessionFilter}
              />
            </div>
            <div className={activeTab === MANAGE_COURSE_WAITLIST_TAB_INDEX ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <CourseWaitlistTab course={course} />
            </div>
            <div className={activeTab === MANAGE_COURSE_TRANSACTIONS_TAB_INDEX ? `${styles.section} ${styles.sectionActive}` : styles.section}>
              <CourseTransactions
                transactions={transactions}
                course={course}
                onRefund={() => {
                  setTransactionsLoaded(false);
                }}
              />
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default CourseManage;
